// Wizard step machine + branch routing. The wizard is source-first: from the hub,
// a chat source (chatgpt/claude/perplexity) needs the extension; google (OAuth),
// claude-code (CLI), github (OAuth) and granola (API key) connect directly; slack and
// notion are team-workspace installs (OAuth) that need a team to exist first (created
// on the fly if the user has none — see TeamSourceConnectStep + useEnsureTeam).

export type WizardStep =
  | 'welcome'
  | 'hub'
  | 'chat-install'
  | 'chat-consent'
  | 'team'
  | 'chat-done'
  | 'google'
  | 'google-done'
  | 'claudecode'
  | 'claudecode-done'
  | 'github'
  | 'github-done'
  | 'slack'
  | 'slack-done'
  | 'notion'
  | 'notion-done'
  | 'granola'
  | 'granola-done';

export type BranchKind = 'chat' | 'google' | 'claudecode' | 'github' | 'slack' | 'notion' | 'granola';

// The hub's source inventory. `recommended` sources render first (the shortlist the mock
// steers new users toward); the rest show under "All sources" so onboarding reflects the
// full breadth of what Terum captures. `ext` = chat source that needs the browser
// extension. `team` = team-workspace install that needs a team + admin (Slack/Notion).
// slack_user (per-user DMs) is deliberately excluded — only the workspace-level Slack
// source belongs in onboarding.
export interface HubSource {
  id: string;
  recommended: boolean;
  ext?: boolean;
  team?: boolean;
}

export const HUB_SOURCES: HubSource[] = [
  { id: 'slack', recommended: true, team: true },
  { id: 'google', recommended: true },
  { id: 'claude-code', recommended: true },
  { id: 'github', recommended: true },
  { id: 'granola', recommended: true },
  { id: 'chatgpt', recommended: false, ext: true },
  { id: 'claude', recommended: false, ext: true },
  { id: 'perplexity', recommended: false, ext: true },
  { id: 'notion', recommended: false, team: true },
];

/** Which connect branch a source belongs to. */
export function deriveBranchKind(sourceId: string): BranchKind {
  switch (sourceId) {
    case 'google': return 'google';
    case 'claude-code': return 'claudecode';
    case 'github': return 'github';
    case 'slack': return 'slack';
    case 'notion': return 'notion';
    case 'granola': return 'granola';
    default: return 'chat'; // chatgpt / claude / perplexity
  }
}

/** The first wizard step for a picked source — chat sources start at install; every other
 *  source jumps straight to its own connect step. */
export function firstStepForSource(sourceId: string): WizardStep {
  switch (deriveBranchKind(sourceId)) {
    case 'chat':
      return 'chat-install';
    case 'google':
      return 'google';
    case 'claudecode':
      return 'claudecode';
    case 'github':
      return 'github';
    case 'slack':
      return 'slack';
    case 'notion':
      return 'notion';
    case 'granola':
      return 'granola';
  }
}
